import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Breadcrumb } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import {
  formatCost,
  formatNumber,
  formatRelativeTime,
  formatTimestamp,
} from '@/lib/formatters';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuthenticatedFetch } from '@/hooks/useAuthenticatedFetch';
import { getCachedData, setCachedData } from '@/lib/cache';

const PAGE_SIZE = 20;

type AiExecutionRecord = {
  id?: string;
  userId?: string;
  timestamp?: number;
  modelId?: string;
  cost?: number | null;
  tokensIn?: number | null;
  tokensOut?: number | null;
  tokensCached?: number | null;
  tokensReasoning?: number | null;
  latency?: number | null;
  [key: string]: unknown;
};

type AiExecutionsResponse = {
  userId: string;
  records: AiExecutionRecord[];
  totalCount: number;
  lastKey?: {
    userId: string;
    id: string;
  };
  hasMore: boolean;
  totals: {
    cost: number;
    tokensIn: number;
    tokensOut: number;
    tokensCached: number;
    tokensReasoning: number;
  };
};

export default function AiExecutionsPage() {
  const { userId } = useParams<{ userId: string }>();
  const authenticatedFetch = useAuthenticatedFetch();
  const [navigationHistory, setNavigationHistory] = useState<(string | null)[]>(
    [null],
  );
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [records, setRecords] = useState<AiExecutionRecord[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [totals, setTotals] = useState<AiExecutionsResponse['totals'] | null>(
    null,
  );
  const [nextPageKey, setNextPageKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    const controller = new AbortController();

    async function fetchExecutions() {
      try {
        const currentLastKey = navigationHistory[currentPageIndex];

        // Check cache first
        const cacheKey = `exec_${userId}_${currentLastKey ?? 'first'}`;
        const cachedData = getCachedData<AiExecutionsResponse>(cacheKey);

        if (cachedData) {
          setRecords(cachedData.records);
          setTotalCount(cachedData.totalCount);
          setTotals(cachedData.totals);
          setNextPageKey(
            cachedData.lastKey ? JSON.stringify(cachedData.lastKey) : null,
          );
          setLoading(false);
          return;
        }

        setLoading(true);
        setError(null);

        // Build URL with lastKey if present
        let url = `users/${userId}/ai-executions?limit=${PAGE_SIZE}`;
        if (currentLastKey) {
          url += `&lastKey=${encodeURIComponent(currentLastKey)}`;
        }

        const response = await authenticatedFetch(url, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch AI executions (${response.status})`);
        }

        const payload = (await response.json()) as AiExecutionsResponse;
        if (controller.signal.aborted) {
          return;
        }

        // Store in cache
        setCachedData(cacheKey, payload);

        setRecords(payload.records);
        setTotalCount(payload.totalCount);
        setTotals(payload.totals);
        setNextPageKey(
          payload.lastKey ? JSON.stringify(payload.lastKey) : null,
        );
      } catch (fetchError) {
        if (controller.signal.aborted) {
          return;
        }
        console.error(fetchError);
        setError(
          fetchError instanceof Error
            ? fetchError.message
            : 'Unable to fetch AI executions.',
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    fetchExecutions();

    return () => controller.abort();
  }, [authenticatedFetch, userId, currentPageIndex, navigationHistory]);

  const totalPages = totalCount
    ? Math.max(Math.ceil(totalCount / PAGE_SIZE), 1)
    : null;
  const hasNextPage = !!nextPageKey;
  const hasPreviousPage = currentPageIndex > 0;

  const handleNextPage = () => {
    if (!nextPageKey) return;

    // Add the next page key to navigation history if it's not already there
    if (currentPageIndex === navigationHistory.length - 1) {
      setNavigationHistory([...navigationHistory, nextPageKey]);
    }
    setCurrentPageIndex(currentPageIndex + 1);
  };

  const handlePreviousPage = () => {
    if (currentPageIndex === 0) return;
    setCurrentPageIndex(currentPageIndex - 1);
  };

  return (
    <div className="space-y-3 sm:space-y-6">
      <Breadcrumb
        items={[
          { label: 'Dashboard', to: '/' },
          { label: 'Users', to: '/users' },
          { label: userId || 'User', to: `/users/${userId}` },
          { label: 'AI Executions' },
        ]}
      />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            AI executions
          </h1>
          <p className="text-muted-foreground">
            Full execution history for user{' '}
            <span className="font-mono">{userId}</span>
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to={`/users/${userId}`}>Back to user</Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Execution history</CardTitle>
          <CardDescription>
            Showing {records.length} executions on page {currentPageIndex + 1}
            {totalCount ? ` (${totalCount.toLocaleString()} total)` : ''}.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 sm:space-y-4">
          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead className="text-right">Latency</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead className="text-right">Tokens In</TableHead>
                  <TableHead className="text-right">Tokens Out</TableHead>
                  <TableHead className="text-right">Cached</TableHead>
                  <TableHead className="text-right">Reasoning</TableHead>
                  <TableHead>Execution ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((record, index) => (
                  <TableRow key={record.id ?? index}>
                    <TableCell className="font-mono">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-muted-foreground">
                          {formatRelativeTime(record.timestamp)}
                        </span>
                        <span className="text-xs">
                          {formatTimestamp(record.timestamp)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{record.modelId ?? '—'}</TableCell>
                    <TableCell className="text-right font-mono">
                      {record.latency != null
                        ? `${Math.round(record.latency)}ms`
                        : '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCost(record.cost)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatNumber(record.tokensIn)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatNumber(record.tokensOut)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatNumber(record.tokensCached)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatNumber(record.tokensReasoning)}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {record.id ?? '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {totals && records.length > 0 && (
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={2}>Totals (this page)</TableCell>
                    <TableCell />
                    <TableCell className="text-right font-mono">
                      {formatCost(totals.cost)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatNumber(totals.tokensIn)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatNumber(totals.tokensOut)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatNumber(totals.tokensCached)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatNumber(totals.tokensReasoning)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              )}
              <TableCaption>
                {loading && 'Loading executions…'}
                {error && <span className="text-destructive">{error}</span>}
                {!loading &&
                  !error &&
                  records.length === 0 &&
                  'No records found.'}
              </TableCaption>
            </Table>
          </div>
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handlePreviousPage}
              disabled={!hasPreviousPage || loading}
            >
              Previous
            </Button>
            <div className="text-sm text-muted-foreground">
              Page {currentPageIndex + 1}
              {totalPages ? ` of ${totalPages}` : ''}
            </div>
            <Button
              variant="outline"
              onClick={handleNextPage}
              disabled={!hasNextPage || loading}
            >
              Next
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
